import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/MoveTimer.css';

const MoveTimer = ({ 
  timeLimit, // seconds per move (null = no limit)
  isActive, // is the game active (not ended)?
  playerColor,
  currentTurn, // 'white' or 'black'
  onTimeOut, // callback when time runs out
  gameId,
  isSpectator = false
}) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const intervalRef = useRef(null);
  const lastTurnRef = useRef(null);
  const onTimeOutRef = useRef(onTimeOut);
  const hasCalledTimeoutRef = useRef(false);
  const currentTurnRef = useRef(currentTurn);
  const playerColorRef = useRef(playerColor);

  // Keep refs updated with latest values
  useEffect(() => {
    onTimeOutRef.current = onTimeOut;
  }, [onTimeOut]);

  useEffect(() => {
    currentTurnRef.current = currentTurn;
  }, [currentTurn]);

  useEffect(() => {
    playerColorRef.current = playerColor;
  }, [playerColor]);

  // Reset timer when turn changes
  useEffect(() => {
    if (timeLimit && currentTurn !== lastTurnRef.current) {
      lastTurnRef.current = currentTurn;
      setTimeLeft(timeLimit);
      setIsWarning(false);
      setIsCritical(false);
      hasCalledTimeoutRef.current = false; // Reset timeout flag on turn change
    }
  }, [currentTurn, timeLimit]);

  // Initialize lastTurnRef on mount
  useEffect(() => {
    if (currentTurn && !lastTurnRef.current) {
      lastTurnRef.current = currentTurn;
    }
  }, [currentTurn]);

  // Timer countdown logic - runs for the current turn player
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!timeLimit || !isActive) {
      return;
    }

    // Spectators still see the timer counting but can't trigger timeout
    // The timer runs for whoever's turn it is, visible to all
    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          
          // Time's up - only the active player sends timeout, not spectators
          // Use refs to get current values at timeout time (avoid stale closure)
          const isCurrentlyMyTurn = playerColorRef.current === currentTurnRef.current;
          if (isCurrentlyMyTurn && !isSpectator && !hasCalledTimeoutRef.current) {
            hasCalledTimeoutRef.current = true;
            if (onTimeOutRef.current) {
              // Use setTimeout to avoid state update during render
              setTimeout(() => onTimeOutRef.current(), 0);
            }
          }
          return 0;
        }
        
        const newTime = prev - 1;
        
        // Warning at 30% time remaining
        if (newTime <= timeLimit * 0.3 && newTime > timeLimit * 0.1) {
          setIsWarning(true);
          setIsCritical(false);
        }
        // Critical at 10% time remaining
        else if (newTime <= timeLimit * 0.1) {
          setIsWarning(false);
          setIsCritical(true);
        }
        
        return newTime;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timeLimit, playerColor, currentTurn, isActive, isSpectator]); // Dependencies include currentTurn

  // Format time display
  const formatTime = useCallback((seconds) => {
    if (seconds === null || seconds === undefined) return '∞';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  }, []);

  // Don't render if no time limit
  if (!timeLimit) {
    return null;
  }

  const isMyTurn = playerColor === currentTurn;
  const percentage = Math.max(0, (timeLeft / timeLimit) * 100);

  return (
    <div 
      className={`move-timer ${isMyTurn ? 'active' : 'waiting'} ${isWarning ? 'warning' : ''} ${isCritical ? 'critical' : ''}`}
      data-testid="move-timer"
    >
      <div className="timer-header">
        <span className="timer-icon">⏱️</span>
        <span className="timer-label">
          {isMyTurn ? 'Your Turn' : `${currentTurn === 'white' ? 'White' : 'Black'}'s Turn`}
        </span>
      </div>
      
      <div className="timer-display">
        <span className="time-value">{formatTime(timeLeft)}</span>
      </div>
      
      <div className="timer-bar-container">
        <div 
          className="timer-bar" 
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {isCritical && isMyTurn && (
        <div className="timer-warning-text">
          ⚠️ Make your move!
        </div>
      )}
    </div>
  );
};

export default MoveTimer;
